import { uniqueId } from 'lodash'
import React, { useState } from 'react'

import styles from './JournalCardHydrationContainer.module.css'

import { Icon, IconColor, IconName } from 'Components'
import { UNITS } from 'Models'
import { useAppDispatch, useAppSelector } from 'ReduxStore/hooks'
import { journalDateSelector } from 'ReduxStore/journalDay'
import {
  addJournalHydrationRecord,
  deleteJournalHydrationRecord,
  selectAmountDrunk,
  selectJournalHydrationGoal,
  selectJournalHydrationRecords
} from 'ReduxStore/journalHydrationRecordsSlice'

const MAX_AMOUNT_DISPLAYED = 5000
export const DEFAULT_GLASS_SIZE = 250

const militliterToLiter = (amount: number): number => {
  if (UNITS.LITER.baseUnit) {
    return amount / UNITS.LITER.baseUnit.factor
  }
  return amount
}

const hydrationGlassContainerClasses = 'cursor p-0 mt-1'

const JournalCardHydrationContainer: React.FC = () => {
  const userSettings = useAppSelector((state) => state.userSettings)
  const glassSize = (userSettings.data.hydrationGlassSize as number) ?? DEFAULT_GLASS_SIZE

  const dispatch = useAppDispatch()
  const [isDeletingGlass, setIsDeletingGlass] = useState(false)

  const hydrationRecords = useAppSelector(selectJournalHydrationRecords)
  const hydrationGoal = useAppSelector(selectJournalHydrationGoal)
  const amountDrunk = useAppSelector(selectAmountDrunk)
  const displayedDay = useAppSelector(journalDateSelector).date

  const numberOfAllGlasses: number = Math.ceil(hydrationGoal / glassSize)
  const numberOfFilledGlasses: number = Math.floor(amountDrunk / glassSize)
  const numberOfEmptyGlasses: number = numberOfAllGlasses - numberOfFilledGlasses

  const isHydrationMaximumReached: boolean = amountDrunk >= MAX_AMOUNT_DISPLAYED
  const isPlusGlassShown: boolean = numberOfEmptyGlasses <= 0 && !isHydrationMaximumReached

  const fadeOut = (): void => {
    setIsDeletingGlass(true)
  }

  const fadeIn = (): void => {
    setTimeout(() => {
      setIsDeletingGlass(false)
    }, 120)
  }

  const addHydrationRecord = async (amount: number): Promise<void> => {
    fadeOut()

    await dispatch(
      addJournalHydrationRecord({
        datetime: displayedDay,
        amount: amount * glassSize - (amountDrunk % glassSize),
        source: 'journal'
      })
    )

    fadeIn()
  }

  const deleteHydrationRecords = async (glassesToDelete: number): Promise<void> => {
    fadeOut()

    const amountToDelete = glassesToDelete * glassSize
    const requests: Promise<unknown>[] = []
    let totalAmount = 0

    for (let i = hydrationRecords.length - 1; i >= 0 && totalAmount < amountToDelete; i--) {
      const record = hydrationRecords[i]
      requests.push(dispatch(deleteJournalHydrationRecord(record.id)))
      totalAmount += record.amount
    }

    await Promise.all(requests)

    if (totalAmount > amountToDelete) {
      await dispatch(
        addJournalHydrationRecord({
          datetime: displayedDay,
          amount: totalAmount - amountToDelete,
          source: 'journal'
        })
      )
    }

    fadeIn()
  }

  const isGoalReached = (index: number): boolean => {
    return index + 1 === numberOfAllGlasses
  }

  const renderFilledGlasses = (): JSX.Element[] =>
    Array.from({ length: numberOfFilledGlasses }).map((_, index) => (
      <span
        onClick={() => deleteHydrationRecords(numberOfFilledGlasses - index)}
        key={uniqueId()}
        className={hydrationGlassContainerClasses}
      >
        <Icon name={IconName.waterfull} color={IconColor.BLUE_2} medium style={{ marginRight: 4 }} />
        {isGoalReached(index) && <div className={styles.checkmarkIcon}></div>}
      </span>
    ))

  const renderEmptyGlasses = (): JSX.Element[] =>
    Array.from({ length: numberOfEmptyGlasses }).map((_, index) => (
      <span onClick={() => addHydrationRecord(index + 1)} key={uniqueId()} className={hydrationGlassContainerClasses}>
        <Icon name={IconName.waterempty} color={IconColor.GRAY_2} medium style={{ marginRight: 4 }} />
      </span>
    ))

  const renderPlusGlass = (): JSX.Element => {
    return (
      <span onClick={() => addHydrationRecord(1)} className={hydrationGlassContainerClasses}>
        <Icon name={IconName.wateremptyplus} color={IconColor.GRAY_2} medium style={{ marginRight: 4 }} />
      </span>
    )
  }

  return (
    <>
      <section className="standardcard pt-1">
        <div className="standardcard-content">
          <div className="standardcard-content standardcard-labelrow pb-2">
            <span>Flüssigkeitszufuhr</span>
            <span>
              {militliterToLiter(amountDrunk)} / {militliterToLiter(hydrationGoal)} l
            </span>
          </div>
          <div className={`row py-2 pl-2 pr-1 ${isDeletingGlass ? styles.fadeOut : styles.fadeIn}`}>
            {renderFilledGlasses()}
            {isPlusGlassShown && renderPlusGlass()}
            {renderEmptyGlasses()}
          </div>
        </div>
      </section>
    </>
  )
}

export default JournalCardHydrationContainer
